import React, { useEffect, useState } from "react";
import { auth, db } from "../../firebase-config";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import "../../assets/styles/ClientShared.css";

const ClientPortalRevision = () => {
  const [clientPortalRevisionLink, setClientPortalRevisionLink] = useState("");
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const questions = [
    "Is the client portal easy to navigate and understand?",
    "Can you successfully log in and log out without issues?",
    "Is the dashboard displaying accurate project and order information?",
    "Do all buttons and links in the portal work correctly?",
    "Are forms (e.g., document uploads, order creation) functioning properly?",
    "Does the portal work well on both desktop and mobile devices?",
    "Are notifications, messages, or updates displaying as expected?",
    "Does the design and layout feel professional and consistent with the brand?",
    "Is any section of the client portal confusing or hard to use?",
    "What improvements or features would you like to see added?",
    "Did you encounter any errors or bugs during your experience?",
    "Is there anything missing you expected to find?",
    "Any additional comments or feedback?"
  ];

  useEffect(() => {
    const fetchClientPortalRevisionData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("User not authenticated.");
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) throw new Error("User document not found.");
        const userData = userSnap.data();
        const projectRef = doc(db, "projects", userData.projectId);
        const projectSnap = await getDoc(projectRef);
        if (projectSnap.exists()) {
          const projectData = projectSnap.data();
          setClientPortalRevisionLink(projectData.revisionDetails?.clientPortalRevision?.link || "");
          setResponses(projectData.revisionDetails?.clientPortalRevision?.form || {});
        } else {
          throw new Error("Project document not found.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchClientPortalRevisionData();
  }, []);

  const handleInputChange = (question, value) => {
    setResponses((prev) => ({ ...prev, [question]: value }));
  };

  const handleSave = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("User not authenticated.");
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) throw new Error("User document not found.");
      const userData = userSnap.data();
      const projectRef = doc(db, "projects", userData.projectId);
      await updateDoc(projectRef, {
        "revisionDetails.clientPortalRevision.form": responses,
      });
      toast.success("Responses saved successfully.");
    } catch (err) {
      toast.error("Failed to save responses: " + err.message);
    }
  };

  return (
    <div className="cp-page cp-page--narrow">
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">Client Portal Revision</h1>
      </div>

      {loading ? (
        <div className="cp-loading"><p>Loading...</p></div>
      ) : (
        <>
          {error && <p className="cp-message--error">{error}</p>}

          <div className="cp-section" style={{marginBottom:'20px'}}>
            <div className="cp-section-header"><h2 className="cp-section-title">Preview Link</h2></div>
            <div className="cp-section-body">
              {clientPortalRevisionLink ? (
                <a
                  href={clientPortalRevisionLink.startsWith('http') ? clientPortalRevisionLink : `https://${clientPortalRevisionLink}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cp-doc-link"
                  style={{fontSize:'0.9375rem'}}
                >
                  View Client Portal →
                </a>
              ) : (
                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',margin:0}}>No link available yet. Check back soon.</p>
              )}
            </div>
          </div>

          <div className="cp-section" style={{marginBottom:'20px'}}>
            <div className="cp-section-header"><h2 className="cp-section-title">Instructions</h2></div>
            <div className="cp-section-body">
              <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.7,marginTop:0}}>
                Please provide detailed and descriptive feedback for each question. Avoid vague responses. Below are examples of good and bad feedback:
              </p>
              <div style={{marginTop:'16px',display:'flex',flexDirection:'column',gap:'8px'}}>
                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'#16a34a',margin:0}}>Good</p>
                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"I had trouble logging out on mobile. The logout button didn't respond."</p>
                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--accent)',margin:'8px 0 0'}}>Avoid</p>
                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"Logout doesn't work."</p>
                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'#16a34a',margin:'8px 0 0'}}>Good</p>
                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"The order tracker didn't update after I uploaded a file. It should reflect changes in real-time."</p>
                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--accent)',margin:'8px 0 0'}}>Avoid</p>
                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"The tracker is broken."</p>
              </div>
            </div>
          </div>

          <div className="cp-section" style={{marginBottom:'20px'}}>
            <div className="cp-section-header"><h2 className="cp-section-title">Feedback Questions</h2></div>
            <div className="cp-section-body">
              {questions.map((question, index) => (
                <div key={index} className="cp-form-group">
                  <label>{question}</label>
                  <textarea
                    value={responses[question] || ''}
                    onChange={e => handleInputChange(question, e.target.value)}
                    placeholder="Write your response here..."
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="cp-btn-row">
            <button className="cp-btn-primary" onClick={handleSave}>Save Responses</button>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientPortalRevision;
